import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink, RouterLinkActive } from '@angular/router';
import { NotificationService } from '../../notification/notification.service';
import { NamePipe } from '@shared/pipes/name.pipe';
import { SessionService } from '@shared/session.service';
import { SocketService } from '@shared/socket.service';

@Component({
  selector: 'app-provably-fair',
  templateUrl: './provably-fair.component.html',
  styleUrls: ['./provably-fair.component.scss'],
  imports: [RouterLink, RouterLinkActive, FormsModule, NamePipe],
})
export class ProvablyFairComponent implements OnInit, OnDestroy {
  selectedGamemode: null | string = null;
  jackpotInfo: {
    hash: string;
    value: number;
    winner: { steamid: string; name: string; avatar: string };
    module: number;
    serialid: number;
    server_seed: string;
    random_seed: string;
  } = null;
  coinflipInfo: {
    hash: string;
    winner: { steamid: string; name: string; avatar: string };
    host: { steamid: string; name: string; avatar: string };
    part: { steamid: string; name: string; avatar: string };
    module: number;
    serialid: number;
    server_seed: string;
    random_seed: string;
  } = null;
  plinkoInfo: {
    serialid: string;
    nonce: number;
    client_seed: string;
  };

  caseUnboxInfo: {
    serialid: string;
    nonce: number;
    client_seed: string;
  };

  caseBattlesInfo: {
    gameid: number;
    serialid: string;
    random_seed: number;
    client_seed: string;
    hash: string;
    tiebreakers: any;
  };

  minesInfo: {
    serialid: string;
    nonce: number;
    client_seed: string;
  };

  towersInfo: {
    serialid: string;
    nonce: number;
    client_seed: string;
  };

  kingInfo: {
    hash: string;
    fled: string;
    round: number;
    king: { steamid: string; name: string; avatar: string; value: number };
    opponent: { steamid: string; name: string; avatar: string; value: number };
    winner: { steamid: string; name: string; avatar: string; value: number };
    module: number;
    serialid: number;
    server_seed: string;
    random_seed: string;
  }[] = null;

  serialidInfo: { server_seed: string; random_seed: string; random: string; signature: string } = null;
  roundId: string;
  clientSeed: string;
  private sub: any;

  constructor(private socketService: SocketService, private route: ActivatedRoute, public session: SessionService, private notificationsService: NotificationService) {}

  async ngOnInit(): Promise<void> {
    this.sub = this.route.params.subscribe((params) => {
      if (params['gamemode']) {
        this.selectGamemode(params['gamemode']);
      }

      if (params['roundid'] && params['gamemode'] && params['roundid'] !== 'null') {
        this.roundId = params['roundid'];
        this.verifyRound();
      }
    });

    await this.session.ready();
    this.clientSeed = this.session.clientSeed;
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  selectGamemode(gamemode: string) {
    this.selectedGamemode = gamemode;
    this.jackpotInfo = null;
    this.coinflipInfo = null;
    this.serialidInfo = null;
    this.plinkoInfo = null;
    this.caseUnboxInfo = null;
    this.caseBattlesInfo = null;
    this.minesInfo = null;
    this.towersInfo = null;
    this.kingInfo = null;
  }

  async verifyRound() {
    const res = await this.socketService.emit('verify_round', { gameid: this.roundId, gamemode: this.selectedGamemode });
    switch (this.selectedGamemode) {
      case 'jackpot':
        this.jackpotInfo = res;
        break;
      case 'coinflip':
        this.coinflipInfo = res;
        break;
      case 'serialId':
        this.serialidInfo = res;
        break;
      case 'plinko':
        this.plinkoInfo = res;
        break;
      case 'caseopening':
        this.caseUnboxInfo = res;
        break;
      case 'casebattles':
        this.caseBattlesInfo = res;
        break;
      case 'mines':
        this.minesInfo = res;
        break;
      case 'towers':
        this.towersInfo = res;
        break;
      case 'king': {
        const kingInfo: any = Object.values(res);

        for (const king of kingInfo) {
          if (king.opponent.value === 0) {
            king.opponent.name = '-1';
          }
          king.fled = king.fled > 0 ? 'Yes' : 'No';
        }

        this.kingInfo = kingInfo;
        break;
      }
    }
  }

  async updateClientSeed() {
    if (this.clientSeed == this.session.clientSeed) {
      this.notificationsService.error('You need to change your client seed first!');
      return;
    }

    await this.session.updateClientSeed(this.clientSeed);
    this.clientSeed = this.session.clientSeed;
  }

  async updateRandomSeed() {
    await this.session.updateRandomSeed();
  }
}
